import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Form, Row, Col } from "react-bootstrap";
import DateField from "@components/FormsV2/DateField/DateField";
import Select2V2 from "@components/FormsV2/Select2/Select2V2";
import GenericToggle from "@components/FormsV2/GenericToggle/GenericToggle";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import { ResolvedIntegration } from "../types/PsaProject";
import { usePsaProjectMutations } from "../api/usePsaProjectMutations";
import useStyles from "../styles/useStyles";

export interface AutotaskFormHandle {
  save: () => void;
}

interface Props {
  integration: ResolvedIntegration;
  projectId: string;
  record?: any;
  readOnly?: boolean;
}

type Opt = { value: string; label: string };

const toOpts = (items?: { id?: string; name?: string }[]): Opt[] =>
  (items ?? []).map((i) => ({ value: String(i.id), label: i.name ?? "" }));
const capOpts = (options?: { value?: any; label?: string }[]): Opt[] =>
  (options ?? []).map((o) => ({
    value: String(o.value),
    label: o.label ?? "",
  }));

const AutotaskForm = forwardRef<AutotaskFormHandle, Props>(
  ({ integration, projectId, record, readOnly }, ref) => {
    const styles = useStyles();
    const { create } = usePsaProjectMutations();
    const ref_ = integration.settings.reference ?? {};
    const cap = integration.settings.capabilities ?? {};

    const opts = record?.attributes?.["project-options"] ?? {};
    const [startOn, setStartOn] = useState<string>(
      record?.attributes?.["start-on"] ?? ""
    );
    const [endOn, setEndOn] = useState<string>(
      record?.attributes?.["end-on"] ?? ""
    );
    const [status, setStatus] = useState<Opt | null>(null);
    const [projectType, setProjectType] = useState<Opt | null>(null);
    const [phases, setPhases] = useState<Opt | null>(null);
    const [subtasks, setSubtasks] = useState<Opt | null>(null);
    const [governance, setGovernance] = useState<boolean>(
      opts.governance === "1"
    );
    const [error, setError] = useState("");
    const [showError, setShowError] = useState(false);

    const statusOptions = toOpts(ref_.statuses?.items);
    const typeOptions = toOpts(ref_.types?.items);
    const phaseOptions = capOpts(cap.phase_options);
    const subtaskOptions = capOpts(cap.subtask_options);

    const findOpt = (list: Opt[], v: any) =>
      list.find((o) => o.value === String(v)) ?? null;

    const save = async () => {
      const body = {
        data: {
          type: "psa-projects",
          attributes: {
            "start-on": startOn,
            "end-on": endOn,
            "project-options": {
              status: status?.value,
              project_type: projectType?.value,
              phases: phases?.value,
              subtasks: subtasks?.value,
              governance: governance ? "1" : "0",
            },
          },
          relationships: {
            "account-connection": {
              data: {
                type: "account-connections",
                id: integration.accountConnectionId,
              },
            },
          },
        },
      } as any;
      try {
        await create(projectId, body);
        // Full-page reload (not client-side navigate) so the record query
        // re-runs and reliably picks up the just-created record — matches
        // Edit.tsx's resubmit() navigation and avoids a stale-cache redirect
        // back to /new (see Edit's guard in PsaProjectMspa.tsx).
        window.location.assign(`/projects/${projectId}/psa_project/edit`);
      } catch (err: any) {
        setError(
          err?.data?.errors?.[0]?.detail ||
            "Something went wrong. Your changes could not be saved."
        );
        setShowError(true);
      }
    };

    useImperativeHandle(ref, () => ({ save }));

    return (
      <>
        {handleAlert(showError, error, setShowError, "warning", ToastAlert)}
        <Form>
          <Row>
            <Col>
              <DateField
                id="autotaskStartOn"
                label="* Estimated Start Date"
                value={startOn}
                onChange={(e) => setStartOn(e.target.value)}
                required
                disabled={readOnly}
              />
            </Col>
            <Col>
              <DateField
                id="autotaskEndOn"
                label="* Estimated End Date"
                value={endOn}
                onChange={(e) => setEndOn(e.target.value)}
                required
                disabled={readOnly}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Select2V2
                label="* Initial Project Status"
                options={statusOptions}
                value={readOnly ? findOpt(statusOptions, opts.status) : status}
                onChange={(e: any) => setStatus(e)}
                isDisabled={readOnly}
              />
            </Col>
            <Col>
              <Select2V2
                label="* Project Type"
                options={typeOptions}
                value={
                  readOnly
                    ? findOpt(typeOptions, opts.project_type)
                    : projectType
                }
                onChange={(e: any) => setProjectType(e)}
                isDisabled={readOnly}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Select2V2
                label="* Add phases to your Autotask project?"
                options={phaseOptions}
                value={readOnly ? findOpt(phaseOptions, opts.phases) : phases}
                onChange={(e: any) => setPhases(e)}
                isDisabled={readOnly}
              />
            </Col>
            <Col>
              <Select2V2
                label="* How should Tasks be created in Autotask?"
                options={subtaskOptions}
                value={
                  readOnly ? findOpt(subtaskOptions, opts.subtasks) : subtasks
                }
                onChange={(e: any) => setSubtasks(e)}
                isDisabled={readOnly}
              />
            </Col>
          </Row>
          <div className={styles.formLabel} style={{ marginTop: 12 }}>
            Create Autotask Tasks for ScopeStack Governance Items?
          </div>
          <div className={styles.toggleWrap}>
            <GenericToggle
              initialValueOfIsOn={governance}
              onToggle={setGovernance}
              onText="Yes"
              offText="No"
              hasText={true}
              disabled={readOnly}
            />
          </div>
        </Form>
      </>
    );
  }
);

AutotaskForm.displayName = "AutotaskForm";
export default AutotaskForm;
